/**
 * useLiveStream — React hook over the OmniSocket singleton.
 *
 * Buffers `assistant.delta` frames into a streaming text string and
 * resolves to the final text when the matching `assistant.final`
 * arrives. Callers send a user.utterance via the returned `say(text)`.
 *
 * Each `say()` call generates a fresh correlation_id so multiple
 * back-to-back utterances don't get their deltas crossed.
 */
import * as React from "react";
import { useAuth } from "@/auth/useAuth";
import {
  getOmniSocket,
  type OmniEnvelopeIn,
  type OmniSocket,
} from "./socket";

import { durationBetween } from "@/components/chat/toolMeta";

/** One tool call, from the tools auditor's ``tool.invoke`` / ``tool.result``. */
export interface LiveToolCall {
  tool_use_id: string;
  /** Chat session the call ran in; the chat shows only its own. */
  session_id: string;
  name: string;
  arguments: Record<string, unknown>;
  status: "pending" | "ok" | "error";
  result?: unknown;
  durationMs?: number;
  /** The tool's declared icon word; on the invoke only. */
  icon?: string | null;
}

export interface LiveStreamState {
  /** Concatenated assistant.delta payloads for the in-flight reply. */
  streaming: string;
  /** Set when an assistant.final arrives. */
  finalText: string | null;
  /** Most recent error, if any. */
  error: string | null;
  /** True between say() and the matching assistant.final. */
  pending: boolean;
  /** User's own speech transcript as reported by the server (``user.transcript``).
   *  Includes incremental partials — drives the ephemeral interim caption. */
  userTranscript: string;
  /** Set only when the user.transcript envelope carries ``final=true``
   *  (i.e. the COMPLETE spoken utterance for one turn). HomePage uses
   *  this to append a user message bubble to the chat history, matching
   *  the typed-utterance flow. Re-set each turn so the same effect fires
   *  again on the next spoken utterance. */
  lastUserUtterance: string | null;
  /** Tool calls seen on this socket, by ``tool_use_id``. Merged here, not
   *  handed over one event at a time: a fast tool's invoke and result
   *  arrive in the same tick, and a single "latest event" state lost the
   *  invoke (and with it the arguments) to React's batching. */
  toolCalls: Record<string, LiveToolCall>;
  say: (text: string, sessionId?: string, idempotencyKey?: string) => void;
}

function newCorrelationId(): string {
  // RFC 4122 v4-ish; not cryptographically strict — server stamps the
  // canonical id. This just needs uniqueness within one tab.
  return `c_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * ``sessionId``: the chat this hook serves. The socket carries every event
 * for the user — all their chats, other tabs, background work — and a reply
 * for another chat used to land in this one (found by the parallel e2e: one
 * spec's answer appeared in the other's thread). Events for another session
 * are ignored; ``undefined`` (no chat yet) takes everything.
 */
export function useLiveStream(sessionId?: string | null): LiveStreamState {
  const auth = useAuth();
  const sessionRef = React.useRef(sessionId);
  sessionRef.current = sessionId;
  const [streaming, setStreaming] = React.useState("");
  const [finalText, setFinalText] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);
  const [userTranscript, setUserTranscript] = React.useState("");
  const [lastUserUtterance, setLastUserUtterance] = React.useState<string | null>(null);
  const [toolCalls, setToolCalls] = React.useState<Record<string, LiveToolCall>>({});
  const correlationRef = React.useRef<string | null>(null);
  const pendingRef = React.useRef(false);

  // True once the session has resolved and a user is signed in.
  const isAuthenticated = auth.ready && auth.user !== null;

  const say = React.useCallback(
    (text: string, sessionId = "s_default", idempotencyKey?: string) => {
      const sock = getOmniSocket(auth.getIdToken);
      const correlation = newCorrelationId();
      const dedupKey = idempotencyKey ?? correlation;
      correlationRef.current = correlation;
      pendingRef.current = true;
      setStreaming("");
      setFinalText(null);
      setError(null);
      setPending(true);
      sock.send({
        type: "user.utterance",
        session_id: sessionId,
        payload: { text, idempotency_key: dedupKey },
        correlation_id: correlation,
      });
    },
    [auth.getIdToken],
  );

  React.useEffect(() => {
    // Only connect once auth is ready and a user is signed in.
    // Without this guard the socket opens with a null token on first
    // render, gets a 1008 close, and the orb shows a WS error immediately.
    if (!isAuthenticated) return;

    const sock: OmniSocket = getOmniSocket(auth.getIdToken);
    const off = sock.onMessage((env: OmniEnvelopeIn) => {
      const mine = sessionRef.current;
      if (mine && env.session_id && env.session_id !== mine) return;
      // Do not strictly filter by correlation_id. The app can mount multiple
      // useLiveStream() subscribers over the singleton socket, and each hook
      // owns a different local correlation ref. Dropping mismatches here makes
      // voice events vanish from whichever subscriber is rendering the call UI.

      if (env.type === "assistant.delta") {
        const delta = String(env.payload.delta ?? "");
        setStreaming((s) => s + delta);
      } else if (env.type === "assistant.final") {
        setFinalText(String(env.payload.text ?? ""));
        setStreaming("");
        setPending(false);
        pendingRef.current = false;
        correlationRef.current = null;
        setUserTranscript(""); // clear user caption when AI reply is done
      } else if (env.type === "turn.started") {
        // AI started speaking — mark pending so the orb shows "thinking"→"speaking"
        setPending(true);
        pendingRef.current = true;
        setStreaming("");
        setError(null);
      } else if (env.type === "turn.ended") {
        setPending(false);
        pendingRef.current = false;
      } else if (env.type === "turn.interrupted") {
        setStreaming("");
        setPending(false);
        pendingRef.current = false;
      } else if (env.type === "user.transcript") {
        // User's own speech transcript, when the server provides one.
        const text = String(env.payload.text ?? env.payload.transcript ?? "");
        if (text) setUserTranscript(text);
        // ``final=true`` is the terminal rollup carrying the COMPLETE
        // utterance for one turn. Push it into ``lastUserUtterance`` so
        // HomePage can append a chat-history bubble for the spoken turn —
        // mirroring how a typed ``user.utterance`` immediately renders.
        // The flag check uses loose equality because some payloads come
        // back as the bare string "true" through JSON dropouts.
        const isFinal =
          env.payload.final === true || env.payload.final === "true";
        if (isFinal && text) {
          setLastUserUtterance(text);
        }
      } else if (env.type === "tool.invoke" || env.type === "tool.result") {
        // Every tool call the agent makes, from the tools auditor. The
        // result can arrive first (separate streams), so neither event
        // overwrites what the other contributed.
        const p = env.payload;
        const id = String(p.run_id ?? "");
        if (!id) return;
        const base = { tool_use_id: id, session_id: env.session_id, name: String(p.tool_id ?? "") };
        setToolCalls((prev) => {
          const seen = prev[id];
          const next: LiveToolCall =
            env.type === "tool.invoke"
              ? {
                  ...base,
                  ...seen,
                  arguments: (p.arguments as Record<string, unknown>) ?? {},
                  icon: typeof p.icon === "string" ? p.icon : null,
                  status: seen?.status ?? "pending",
                }
              : {
                  ...seen,
                  ...base,
                  arguments: seen?.arguments ?? {},
                  status: p.status === "ok" ? "ok" : "error",
                  result: p.status === "ok" ? p.content : p.error,
                  durationMs: durationBetween(p.started_at, p.finished_at),
                };
          return { ...prev, [id]: next };
        });
      } else if (env.type === "error" || env.type === "system.error") {
        setError(String(env.payload.message ?? env.payload.error ?? "Unknown error"));
        setStreaming("");
        setPending(false);
        pendingRef.current = false;
        correlationRef.current = null;
      }
    });
    const offClose = sock.onClose((ev) => {
      if (ev.code !== 1000) {
        setError(`ws closed: ${ev.code}`);
        setStreaming("");
        setPending(false);
        pendingRef.current = false;
        correlationRef.current = null;
      }
    });
    const offOpen = sock.onOpen(() => {
      setError(null);
    });
    return () => {
      off();
      offClose();
      offOpen();
    };
  }, [auth.getIdToken, isAuthenticated]);

  return { streaming, finalText, error, pending, userTranscript, lastUserUtterance, toolCalls, say };
}
